/**
 * Workflow Store
 * 
 * This module provides a Zustand store for managing workflow state across the application.
 * It centralizes the state management for the workflow process, including form data,
 * view states, and results.
 * 
 * NOTE: This is a reference implementation. The actual WorkflowPage component
 * currently uses React's useState hooks for state management. This store can be
 * used to refactor the component for better state management.
 * 
 * @module store/useWorkflowStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LinkedChecklistScanEntry } from '../lib/api-central-mu';

/**
 * Represents an item in the imported checklist data
 */
export interface ImportedChecklistItem {
  id: string;
  displayName: string;
  type: "OBJECT_TYPE" | "OBJECT" | "RELATION" | "PROPERTY";
  externalId: string;
  status: "MATCH" | "MISSING" | "CONFLICT" | "SYNC_FAILED";
  objectTypeName: string;
}

/**
 * Represents an ontology record from the API
 */
export interface OntologyRecord {
  id: string;
  externalId: string;
  displayName: string;
  type: string;
  status: "MATCH" | "MISSING" | "CONFLICT" | "SYNC_FAILED";
  objectTypeName: string;
  referenceObjectTypeId: string;
  referencePropertyId: string | null;
}

/**
 * Represents a checklist from the API
 */
export interface ChecklistItem {
  id: string;
  name: string;
  code: string;
  state: string;
  version: number;
  archived: boolean;
  properties: Array<{
    id: string;
    name: string;
    label: string;
    value: string;
  }>;
  audit: {
    createdAt: number;
    modifiedAt: number;
    createdBy: {
      id: string;
      employeeId: string;
      firstName: string;
      lastName: string;
    };
    modifiedBy: {
      id: string;
      employeeId: string;
      firstName: string;
      lastName: string;
    };
  };
  ancestorId: string;
  colorCode: string;
  global: boolean;
}

/**
 * Workflow state interface
 * 
 * @interface WorkflowState
 */
interface WorkflowState {
  // State properties
  formData: Record<string, any>;
  selectedService: any | null;
  databaseOptions: any[];
  loadingDatabases: boolean;
  databaseError: string | null;
  isFormValid: boolean;
  selectedFileName: string | null;
  selectedChecklists: ChecklistItem[];
  checklistSearchTerm: string;
  viewState: 'form' | 'scan-review' | 'target-selection' | 'linked' | 'loading' | 'results' | 'migrating' | 'success';
  checklistResults: ImportedChecklistItem[];
  ontologyRecords: OntologyRecord[]; // Store ontology records from API
  autoIdObjectTypeIds: string[]; // Source ObjectType ids that carry an active Auto-ID config
  counterModeByObjectType: Record<string, 'NEW' | 'PREVIOUS'>; // Per-OT counter choice for Auto-ID migration
  selectedItems: Record<string, boolean>; // Track which items are selected by ID
  selectedOntologyRecords: Record<string, boolean>; // Track which ontology records are selected by ID
  groupedResults: Record<string, number>;
  currentWorkflow: {
    id: string | null;
    name: string | null;
    type: string | null;
    description?: string | null;
  } | null;
  selectedFacilityId: string | null;
  selectedUseCaseId: string | null;
  selectedUseCaseName: string | null;
  selectedServiceId: string | null;
  selectedClient: any | null; // Store the full client object
  includeObjects: boolean; // When true, ontology migration also pulls in referenced EntityObjects

  // Target instance properties
  targetServiceId: string | null;
  targetFacilityId: string | null;
  targetFacilityName: string | null;
  targetUseCaseId: string | null;
  targetUseCaseName: string | null;
  targetClient: any | null; // Store the full target client object
  targetImportType: 'new' | 'revision' | null; // Import type: new checklist or revision
  targetChecklistId: string | null; // For revision: the checklist ID to revise against
  targetChecklistName: string | null; // For revision: the picked checklist's name (for the confirm dialog)
  targetAuthorId: string | null; // Store the selected target author ID

  // Linked-process scan (selection step). null ⇒ scan unavailable → legacy whole-selection routing.
  linkedScan: LinkedChecklistScanEntry[] | null;
  // Which subset the plain review/confirm covers (ids). null ⇒ all of selectedChecklists.
  plainImportChecklistIds: string[] | null;

  // Actions
  createNewWorkflow: (type: string) => string;
  setCurrentWorkflow: (workflow: { id: string; name: string; type: string; description?: string }) => void;
  updateWorkflowName: (name: string) => void;
  updateWorkflowDescription: (description: string) => void;
  setFormData: (data: Record<string, any>) => void;
  updateFormField: (id: string, value: any) => void;
  setSelectedService: (service: any | null) => void;
  setDatabaseOptions: (options: any[]) => void;
  setLoadingDatabases: (loading: boolean) => void;
  setDatabaseError: (error: string | null) => void;
  setIsFormValid: (valid: boolean) => void;
  setSelectedFileName: (fileName: string | null) => void;
  setSelectedChecklists: (checklists: ChecklistItem[]) => void;
  addSelectedChecklist: (checklist: ChecklistItem) => void;
  removeSelectedChecklist: (checklistId: string) => void;
  clearSelectedChecklists: () => void;
  setChecklistSearchTerm: (term: string) => void;
  setSelectedFacilityId: (facilityId: string | null) => void;
  setSelectedUseCaseId: (useCaseId: string | null) => void;
  setSelectedUseCaseName: (useCaseName: string | null) => void;
  setSelectedServiceId: (serviceId: string | null) => void;
  setSelectedClient: (client: any | null) => void;
  setIncludeObjects: (include: boolean) => void;
  setViewState: (state: 'form' | 'scan-review' | 'target-selection' | 'linked' | 'loading' | 'results' | 'migrating' | 'success') => void;
  
  // Target instance actions
  setTargetServiceId: (serviceId: string | null) => void;
  setTargetFacilityId: (facilityId: string | null) => void;
  setTargetFacilityName: (facilityName: string | null) => void;
  setTargetUseCaseId: (useCaseId: string | null) => void;
  setTargetUseCaseName: (useCaseName: string | null) => void;
  setTargetClient: (client: any | null) => void;
  setTargetImportType: (importType: 'new' | 'revision' | null) => void;
  setTargetChecklistId: (checklistId: string | null) => void;
  setTargetChecklistName: (checklistName: string | null) => void;
  setTargetAuthorId: (authorId: string | null) => void;
  setLinkedScan: (entries: LinkedChecklistScanEntry[] | null) => void;
  setPlainImportChecklistIds: (ids: string[] | null) => void;
  setChecklistResults: (results: ImportedChecklistItem[]) => void;
  setOntologyRecords: (records: OntologyRecord[]) => void; // Set ontology records
  setCounterMode: (objectTypeId: string, mode: 'NEW' | 'PREVIOUS') => void; // Set per-OT Auto-ID counter choice
  setGroupedResults: (results: Record<string, number>) => void;
  toggleItemSelection: (id: string, selected: boolean) => void; // Toggle selection of an item
  toggleOntologyRecordSelection: (id: string, selected: boolean) => void; // Toggle selection of an ontology record
  resetForm: () => void;
  processChecklistResults: (results: ImportedChecklistItem[]) => void;
  processOntologyRecords: (response: { ontologyRecords: OntologyRecord[]; autoIdObjectTypeIds?: string[] }) => void; // Process ontology records from API
  getSelectedItems: () => ImportedChecklistItem[]; // Get only the selected items
  getSelectedOntologyRecords: () => OntologyRecord[]; // Get only the selected ontology records
}

/**
 * Initial grouped results state
 */
const initialGroupedResults = {
  OBJECT_TYPE: 0,
  OBJECT: 0,
  RELATION: 0,
  PROPERTY: 0
};

/**
 * Workflow store using Zustand with persistence
 * 
 * This store manages the workflow state and provides actions for manipulating it.
 * The state is persisted in localStorage to survive page refreshes and navigation.
 * 
 * @example
 * // Using the workflow store in a component
 * const { 
 *   formData, 
 *   viewState, 
 *   updateFormField, 
 *   setViewState 
 * } = useWorkflowStore();
 * 
 * // To update a form field
 * updateFormField('host', '123');
 * 
 * // To change the view state
 * setViewState('loading');
 */
export const useWorkflowStore = create<WorkflowState>()(
  persist(
    (set, get) => ({
      // Initial state
      formData: {},
      selectedService: null,
      databaseOptions: [],
      loadingDatabases: false,
      databaseError: null,
      isFormValid: false,
      selectedFileName: null,
      selectedChecklists: [],
      checklistSearchTerm: "",
      viewState: 'form',
      checklistResults: [],
      ontologyRecords: [], // Initialize empty ontology records array
      autoIdObjectTypeIds: [],
      counterModeByObjectType: {},
      selectedItems: {}, // Empty object to track selected items
      selectedOntologyRecords: {}, // Empty object to track selected ontology records
      groupedResults: initialGroupedResults,
      currentWorkflow: null,
      selectedFacilityId: null,
      selectedUseCaseId: null,
      selectedUseCaseName: null,
      selectedServiceId: null,
      selectedClient: null,
      includeObjects: false,

      // Target instance initial state
      targetServiceId: null,
      targetFacilityId: null,
      targetFacilityName: null,
      targetUseCaseId: null,
      targetUseCaseName: null,
      targetClient: null,
      targetImportType: null,
      targetChecklistId: null,
      targetChecklistName: null,
      targetAuthorId: null,
      linkedScan: null,
      plainImportChecklistIds: null,

      /**
       * Create a new workflow and set it as the current workflow
       * @param {string} type - The type of workflow to create (e.g., "Ontology Migration")
       * @returns {string} The ID of the newly created workflow
       */
      createNewWorkflow: (type) => {
        // Generate a unique ID with format ONT-MMMDD-XX (e.g., ONT-JUN25-15)
        const date = new Date();
        const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
        const id = `${type === 'Ontology Migration' ? 'ONT' : 'PROC'}-${month}${day}-${random}`;
        
        // Create the workflow object
        const workflow = {
          id,
          name: `${type} ${id}`,
          type,
          description: `${type} created on ${date.toLocaleDateString()}`
        };
        
        // Set it as the current workflow
        set({ 
          currentWorkflow: workflow,
          viewState: 'form'
        });
        
        return id;
      },
      
      /**
       * Set the current workflow
       * @param {{ id: string; name: string; type: string; description?: string }} workflow - The workflow object
       */
      setCurrentWorkflow: (workflow) => set({
        currentWorkflow: workflow,
        viewState: 'form',  // Reset viewState to form when setting a new workflow
        // Reset ALL workflow-related state
        formData: {},
        selectedChecklists: [],
        checklistResults: [],
        ontologyRecords: [],
        selectedItems: {},
        selectedOntologyRecords: {},
        groupedResults: initialGroupedResults,
        // Reset selection state
        selectedServiceId: null,
        selectedClient: null,
        selectedFacilityId: null,
        selectedUseCaseId: null,
        selectedUseCaseName: null,
        includeObjects: false,
        // Reset linked-process scan + split-routing state
        linkedScan: null,
        plainImportChecklistIds: null,
        // Reset other form state
        selectedFileName: null,
        checklistSearchTerm: "",
        isFormValid: false,
        databaseOptions: [],
        loadingDatabases: false,
        databaseError: null,
      }),
      
      /**
       * Update the workflow name
       * @param {string} name - The new workflow name
       */
      updateWorkflowName: (name) => set((state) => ({
        currentWorkflow: state.currentWorkflow 
          ? { ...state.currentWorkflow, name } 
          : null
      })),
      
      /**
       * Update the workflow description
       * @param {string} description - The new workflow description
       */
      updateWorkflowDescription: (description) => set((state) => ({
        currentWorkflow: state.currentWorkflow 
          ? { ...state.currentWorkflow, description } 
          : null
      })),
      
      // Actions
      
      /**
       * Set the entire form data object
       * @param {Record<string, any>} data - The form data to set
       */
      setFormData: (data) => set({ formData: data }),
      
      /**
       * Update a single form field
       * @param {string} id - The field ID
       * @param {any} value - The field value
       */
      updateFormField: (id, value) => set((state) => ({
        formData: {
          ...state.formData,
          [id]: value,
        }
      })),
      
      /**
       * Set the selected service
       * @param {any | null} service - The selected service object
       */
      setSelectedService: (service) => set({ selectedService: service }),
      
      /**
       * Set the database options
       * @param {any[]} options - The database options
       */
      setDatabaseOptions: (options) => set({ databaseOptions: options }),
      
      /**
       * Set the loading state for databases
       * @param {boolean} loading - Whether databases are loading
       */
      setLoadingDatabases: (loading) => set({ loadingDatabases: loading }),
      
      /**
       * Set the database error
       * @param {string | null} error - The error message or null
       */
      setDatabaseError: (error) => set({ databaseError: error }),
      
      /**
       * Set the form validity
       * @param {boolean} valid - Whether the form is valid
       */
      setIsFormValid: (valid) => set({ isFormValid: valid }),
      
      /**
       * Set the selected file name
       * @param {string | null} fileName - The selected file name or null
       */
      setSelectedFileName: (fileName) => set({ selectedFileName: fileName }),
      
      /**
       * Set the selected checklists
       * @param {ChecklistItem[]} checklists - The checklists to set
       */
      setSelectedChecklists: (checklists) => set({ selectedChecklists: checklists }),
      
      /**
       * Add a checklist to the selected checklists
       * @param {ChecklistItem} checklist - The checklist to add
       */
      addSelectedChecklist: (checklist) => set((state) => {
        // Check if the checklist is already selected
        const isAlreadySelected = state.selectedChecklists.some(
          (item) => item.id === checklist.id
        );
        
        // If already selected, return the current state
        if (isAlreadySelected) {
          return state;
        }
        
        // Otherwise, add the checklist to the selected checklists
        return {
          selectedChecklists: [...state.selectedChecklists, checklist],
        };
      }),
      
      /**
       * Remove a checklist from the selected checklists
       * @param {string} checklistId - The ID of the checklist to remove
       */
      removeSelectedChecklist: (checklistId) => set((state) => ({
        selectedChecklists: state.selectedChecklists.filter(
          (checklist) => checklist.id !== checklistId
        ),
      })),
      
      /**
       * Clear all selected checklists
       */
      clearSelectedChecklists: () => set({ selectedChecklists: [] }),
      
      /**
       * Set the checklist search term
       * @param {string} term - The search term
       */
      setChecklistSearchTerm: (term) => set({ checklistSearchTerm: term }),
      
      /**
       * Set the selected facility ID
       * @param {string | null} facilityId - The facility ID to set
       */
      setSelectedFacilityId: (facilityId) => set({ selectedFacilityId: facilityId }),
      
      /**
       * Set the selected use case ID
       * @param {string | null} useCaseId - The use case ID to set
       */
      setSelectedUseCaseId: (useCaseId) => set({ selectedUseCaseId: useCaseId }),

      /** Set the selected (source) use case display name — shown on object-instance audit lines. */
      setSelectedUseCaseName: (useCaseName) => set({ selectedUseCaseName: useCaseName }),

      /**
       * Set the selected service ID
       * @param {string | null} serviceId - The service ID to set
       */
      setSelectedServiceId: (serviceId) => set({ selectedServiceId: serviceId }),
      
      /**
       * Set the selected client
       * @param {any | null} client - The client object to set
       */
      setSelectedClient: (client) => set({ selectedClient: client }),

      /**
       * Toggle whether to also migrate referenced EntityObjects in the ontology flow.
       */
      setIncludeObjects: (include) => set({ includeObjects: include }),

      /**
       * Set the target service ID
       * @param {string | null} serviceId - The target service ID to set
       */
      setTargetServiceId: (serviceId) => set({ targetServiceId: serviceId }),
      
      /**
       * Set the target facility ID
       * @param {string | null} facilityId - The target facility ID to set
       */
      setTargetFacilityId: (facilityId) => set({ targetFacilityId: facilityId }),
      setTargetFacilityName: (facilityName) => set({ targetFacilityName: facilityName }),
      
      /**
       * Set the target use case ID
       * @param {string | null} useCaseId - The target use case ID to set
       */
      setTargetUseCaseId: (useCaseId) => set({ targetUseCaseId: useCaseId }),

      /** Set the target use case display name — shown on object-instance audit lines. */
      setTargetUseCaseName: (useCaseName) => set({ targetUseCaseName: useCaseName }),

      /**
       * Set the target client
       * @param {any | null} client - The target client object to set
       */
      setTargetClient: (client) => set({ targetClient: client }),
      
      /**
       * Set the target import type (new or revision)
       * @param {string | null} importType - The import type
       */
      setTargetImportType: (importType) => set({ targetImportType: importType }),

      /**
       * Set the target checklist ID for revision
       * @param {string | null} checklistId - The checklist ID to revise against
       */
      setTargetChecklistId: (checklistId) => set({ targetChecklistId: checklistId }),
      setTargetChecklistName: (checklistName) => set({ targetChecklistName: checklistName }),

      /**
       * Set the target author ID
       * @param {string | null} authorId - The target author ID to set
       */
      setTargetAuthorId: (authorId) => set({ targetAuthorId: authorId }),

      setLinkedScan: (entries) => set({ linkedScan: entries }),

      setPlainImportChecklistIds: (ids) => set({ plainImportChecklistIds: ids }),

      /**
       * Set the view state
       * @param {'form' | 'target-selection' | 'loading' | 'results' | 'migrating' | 'success'} state - The view state
       */
      setViewState: (state) => set({ viewState: state }),
      
      /**
       * Set the checklist results
       * @param {ImportedChecklistItem[]} results - The checklist results
       */
      setChecklistResults: (results) => {
        // Initialize selectedItems with MISSING items selected by default
        const selectedItems: Record<string, boolean> = {};
        results.forEach(item => {
          selectedItems[item.id] = item.status === "MISSING";
        });
        
        set({ 
          checklistResults: results,
          selectedItems
        });
      },
      
      
      /**
       * Set the grouped results
       * @param {Record<string, number>} results - The grouped results
       */
      setGroupedResults: (results) => set({ groupedResults: results }),
      
      /**
       * Toggle selection of an item
       * @param {string} id - The ID of the item to toggle
       * @param {boolean} selected - Whether the item is selected
       */
      toggleItemSelection: (id, selected) => set(state => ({
        selectedItems: {
          ...state.selectedItems,
          [id]: selected
        }
      })),
      
      /**
       * Get only the selected items
       * @returns {ImportedChecklistItem[]} The selected items
       */
      getSelectedItems: () => {
        const { checklistResults, selectedItems } = get();
        return checklistResults.filter(item => selectedItems[item.id]);
      },
      
      /**
       * Reset the form state
       * Clears all form-related state and sets view state to 'form'
       */
      resetForm: () => set({
        // Reset form data
        formData: {},
        selectedFileName: null,
        selectedChecklists: [],
        checklistSearchTerm: "",
        checklistResults: [],
        selectedItems: {},
        selectedOntologyRecords: {},
        ontologyRecords: [],
        groupedResults: initialGroupedResults,
        // Reset view state
        viewState: 'form',
        // Reset selection state
        selectedServiceId: null,
        selectedClient: null,
        selectedFacilityId: null,
        selectedUseCaseId: null,
        selectedUseCaseName: null,
        includeObjects: false,
        // Reset target selection state
        targetServiceId: null,
        targetClient: null,
        targetFacilityId: null,
        targetFacilityName: null,
        targetUseCaseId: null,
        targetUseCaseName: null,
        targetImportType: null,
        targetChecklistId: null,
        targetChecklistName: null,
        targetAuthorId: null,
        // Reset linked-process scan + split-routing state
        linkedScan: null,
        plainImportChecklistIds: null,
        // Reset other form state
        isFormValid: false,
        databaseOptions: [],
        loadingDatabases: false,
        databaseError: null,
      }),

      /**
       * Process checklist results and group them by type
       * @param {ImportedChecklistItem[]} results - The checklist items returned from the API
       */
      processChecklistResults: (results) => {
        const grouped = {
          OBJECT_TYPE: 0,
          OBJECT: 0,
          RELATION: 0,
          PROPERTY: 0
        };
        
        results.forEach(item => {
          if (item.type in grouped) {
            grouped[item.type as keyof typeof grouped]++;
          }
        });
        
        set({ 
          groupedResults: grouped,
          checklistResults: results
        });
      },
      
      /**
       * Set ontology records
       * @param {OntologyRecord[]} records - The ontology records to set
       */
      setOntologyRecords: (records) => set({ ontologyRecords: records }),

      /** Set the New/Previous Auto-ID counter choice for one ObjectType. */
      setCounterMode: (objectTypeId, mode) => set(state => ({
        counterModeByObjectType: { ...state.counterModeByObjectType, [objectTypeId]: mode }
      })),
      
      /**
       * Toggle selection of an ontology record
       * @param {string} id - The ID of the ontology record to toggle
       * @param {boolean} selected - Whether the record is selected
       */
      toggleOntologyRecordSelection: (id, selected) => set(state => ({
        selectedOntologyRecords: {
          ...state.selectedOntologyRecords,
          [id]: selected
        }
      })),
      
      /**
       * Process ontology records from API response
       * @param {{ ontologyRecords: OntologyRecord[] }} response - The API response containing ontology records
       */
      processOntologyRecords: (response) => {
        const { ontologyRecords } = response;
        const autoIdObjectTypeIds = response.autoIdObjectTypeIds ?? [];

        // Default every Auto-ID ObjectType to a fresh (NEW) counter; the user can switch to PREVIOUS.
        const counterModeByObjectType: Record<string, 'NEW' | 'PREVIOUS'> = {};
        autoIdObjectTypeIds.forEach(id => { counterModeByObjectType[id] = 'NEW'; });

        // Calculate grouped results from the type field
        const grouped = {
          OBJECT_TYPE: 0,
          OBJECT: 0,
          RELATION: 0,
          PROPERTY: 0
        };
        
        ontologyRecords.forEach(record => {
          if (record.type in grouped) {
            grouped[record.type as keyof typeof grouped]++;
          }
        });
        
        // Initialize selectedOntologyRecords with MISSING items selected by default
        const selectedOntologyRecords: Record<string, boolean> = {};
        ontologyRecords.forEach(record => {
          selectedOntologyRecords[record.id] = record.status === "MISSING";
        });
        
        set({
          ontologyRecords,
          autoIdObjectTypeIds,
          counterModeByObjectType,
          selectedOntologyRecords,
          groupedResults: grouped  // Add statistics
        });
      },
      
      /**
       * Get only the selected ontology records
       * @returns {OntologyRecord[]} The selected ontology records
       */
      getSelectedOntologyRecords: () => {
        const { ontologyRecords, selectedOntologyRecords } = get();
        return ontologyRecords.filter(record => selectedOntologyRecords[record.id]);
      }
    }),
    {
      name: 'workflow-storage', // name of the item in localStorage
      partialize: (state) => ({
        // Only persist these fields
        currentWorkflow: state.currentWorkflow,
        selectedFacilityId: state.selectedFacilityId,
        selectedUseCaseId: state.selectedUseCaseId,
        selectedUseCaseName: state.selectedUseCaseName,
        // viewState is intentionally not persisted to ensure new workflows start at the form state
      }),
    }
  )
);

/**
 * Example of how to use the workflow store with async operations
 * 
 * @example
 * // In a component
 * const {
 *   formData,
 *   viewState,
 *   setViewState,
 *   processChecklistResults
 * } = useWorkflowStore();
 * 
 * const handleNext = async () => {
 *   try {
 *     setViewState('loading');
 *     // Show loading state
 *     
 *     const apiFormData = new FormData();
 *     apiFormData.append('file', formData['source-checklist']);
 *
 *     const response = await fetch('/v1/api/checklists/import', {
 *       method: 'POST',
 *       body: apiFormData,
 *     });
 *     
 *     if (!response.ok) {
 *       throw new Error(`Failed to import checklist: ${response.status}`);
 *     }
 *     
 *     const data = await response.json();
 *     
 *     processChecklistResults(data);
 *     setViewState('results');
 *   } catch (error) {
 *     console.error('Error importing checklist:', error);
 *     // Show error toast
 *     toast({
 *       variant: "destructive",
 *       title: "Import Failed",
 *       description: error instanceof Error ? error.message : "Failed to import checklist",
 *     });
 *     setViewState('form');
 *   }
 * };
 */
